import React from 'react';
import { useQuery } from '@tanstack/react-query';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Image from 'next/legacy/image';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import PropTypes from 'prop-types';

import { colors } from '@startengine/design-system.theme.main';
import { se3Colors, se3FontFamily } from '@startengine/design-system.se3.tokens';
import { PhoneGrid } from './styles';
import { imagePath } from 'utils/helpers';
import { getSalesAgentCalendar } from 'services/user';
import useIsEditorialReskin from 'components/Offering/BuilderLayout/components/shared/useIsEditorialReskin';
import { MUTED_TEXT, TEAL } from 'components/Offering/BuilderLayout/editorial/editorialSection';

const DEFAULT_LABEL = 'Schedule a call with an Associate';

const SEContact = ({ calendarUrlOverride = '', label = DEFAULT_LABEL, ...rest }) => {
  const isEditorialReskin = useIsEditorialReskin();
  const { data, isLoading } = useQuery({
    queryKey: ['salesAgentCalendar'],
    queryFn: () => getSalesAgentCalendar(),
    enabled: !calendarUrlOverride,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (!calendarUrlOverride && isLoading) {
    if (isEditorialReskin) {
      return (
        <Grid item xs={12} {...rest}>
          <Box
            data-cy="editorial-legacy-schedule-call"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '13px 16px',
              borderRadius: '12px',
              border: `1px solid ${se3Colors.edge}`,
              backgroundColor: se3Colors.cream2,
            }}
          >
            <Skeleton variant="circular" width={36} height={36} />
            <Skeleton width={180} height={32} data-cy="editorial-schedule-call-loading" />
          </Box>
        </Grid>
      );
    }

    return (
      <PhoneGrid item xs={12} {...rest}>
        <Grid container alignItems="center" justifyContent="center">
          <Skeleton width="100%" height={52} />
        </Grid>
      </PhoneGrid>
    );
  }

  const calendarUrl =
    calendarUrlOverride ||
    data?.calendarUrl ||
    'https://meetings-na2.hubspot.com/start-engine/startengine-meeting';

  if (isEditorialReskin) {
    return (
      <Grid item xs={12} {...rest}>
        <Box
          data-cy="editorial-legacy-schedule-call"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '13px 16px',
            borderRadius: '12px',
            border: `1px solid ${se3Colors.edge}`,
            backgroundColor: se3Colors.cream2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: se3Colors.card,
              border: `1px solid ${se3Colors.edge}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HeadsetMicOutlinedIcon sx={{ fontSize: 18, color: TEAL }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: se3FontFamily.sans,
                fontSize: '12px',
                fontWeight: 600,
                color: se3Colors.ink,
                lineHeight: 1.3,
              }}
            >
              Need help?
            </Box>
            <Box
              component="a"
              href={calendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-cy="editorial-schedule-call-link"
              sx={{
                fontFamily: se3FontFamily.sans,
                fontSize: '12px',
                fontWeight: 600,
                color: TEAL,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                display: 'inline-block',
              }}
            >
              {label}
            </Box>
            <Box
              sx={{
                fontFamily: se3FontFamily.sans,
                fontSize: '10.5px',
                color: MUTED_TEXT,
                marginTop: '2px',
                letterSpacing: '0.02em',
              }}
            >
              9AM – 5PM PST · Mon – Fri
            </Box>
          </Box>
        </Box>
      </Grid>
    );
  }

  return (
    <PhoneGrid item xs={12} {...rest}>
      <Grid container alignItems="center">
        <Grid item mt="5px">
          <Image
            src={`${imagePath}/images/contact-us/headphone.svg`}
            alt="Headphone"
            height={52}
            width={52}
          />
        </Grid>
        <Grid item ml="5px">
          <Typography
            color="common.tabsTextColor"
            fontSize="16px"
            fontWeight="500"
            sx={{ lineHeight: '24px' }}
          >
            Need Help?{' '}
          </Typography>
          <a
            href={calendarUrl}
            style={{
              textDecoration: 'none',
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography variant="p1Bold" color={colors.SEDark} textAlign="center" component="span">
              {label}
            </Typography>
          </a>
          <Typography variant="p3" color="common.tabsTextColor">
            9AM - 5PM PST • Mon - Fri
          </Typography>
        </Grid>
      </Grid>
    </PhoneGrid>
  );
};

SEContact.propTypes = {
  calendarUrlOverride: PropTypes.string,
  label: PropTypes.string,
};

export default SEContact;
